import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import i18n from '@/i18n';
import router from '@/router';
import {
  deleteShipmentHistory, duplicateShipment, getAddressMap, ShipmentsQueryParams,
} from '@/features/shipment-history/services/shipment-history.services';
import store from '@/features/common/store';
import { ActionsException, HistoryItemType } from '@/features/shipment-history/types';
import {
  HistoryTrackingResponse, PrintLabelSize, ShipFrom, ShipTo,
} from '@/features/shipment/types';
import {
  downloadURI, showErrorAlertDialog, showSuccessAlertDialog, showWarningAlertDialog,
} from '@/features/common/helpers';
import { getCountryNameFromCode, refetchInProgressDataAndSave, showEditShipmentDialog } from '@/features/shipment/helpers/createShipment';
import { reschedulePickup } from '@/features/schedule-pickup/services/schedulePickup.services';
import { getShipmentPrintLabelMultipleAsZip } from '@/features/shipment/services/shipment.services';
import { EstimateDataType } from '@/features/estimate/types';

export const COLUMNS_WITHOUT_TITLE = ['Carrier', 'PackageType', 'ShipStatus'];

export interface EncodedMapImages {
  from: string,
  to: string,
}

export const reprintLabel = async (
  historyItems: HistoryItemType[],
  documentSize: keyof typeof PrintLabelSize = 'Thermal',
  getQrCode = false,
): Promise<void> => {
  if (historyItems.some((item) => item.deleted)) {
    showWarningAlertDialog(i18n.t('history.alertDialog.cannotReprintDeletedShipment') as string);
    return;
  }

  if (historyItems.length === 1) {
    router.push({
      name: 'shipment-print',
      query: {
        id: historyItems[0].trackingNumber,
      },
    });
    return;
  }

  if (historyItems.length > 20) {
    showErrorAlertDialog(i18n.t('shipment.errorMessages.DownloadPrintDocumentsLimitException') as string);
    return;
  }

  store.commit('setLoading', true);
  const response = await getShipmentPrintLabelMultipleAsZip(
    historyItems.map((item) => item.trackingNumber),
    documentSize,
    getQrCode,
  );
  store.commit('setLoading', false);

  if (!response.successful) {
    showErrorAlertDialog(response.errorMessage);
    return;
  }

  const blobZipResponse = new Blob([response.data as BlobPart], { type: 'application/zip' });
  const url = window.URL.createObjectURL(blobZipResponse);
  downloadURI(url, `labels-${documentSize}`);

  showSuccessAlertDialog(i18n.t('shipment.alertDialog.labelsDownloadedSuccessfully') as string);
};

export const getEstimate = async (shipment: HistoryItemType): Promise<void> => {
  const {
    fromAddress, toAddress, signatureOption, packages, isDocumentsOnly,
  } = shipment;

  const payload: EstimateDataType = {
    fromAddress,
    toAddress,
    signatureOptions: signatureOption,
    packages,
    isDocumentsOnly: isDocumentsOnly || false,
  };

  store.commit('setEstimateData', payload);

  router.push({
    name: 'estimate-results',
    query: {
      serviceLevel: shipment.selectedService,
      shipDateTime: shipment.shipDateTime,
    },
  });
};

export const deleteShipments = (
  shipments: HistoryItemType[],
): void => {
  const undeletedShipments = shipments.filter((s) => !s.deleted);

  if (!undeletedShipments.length) {
    showWarningAlertDialog(
      i18n.t(`history.alertDialog.${
        shipments.length > 1
          ? 'shipmentsAlreadyDeleted'
          : 'shipmentAlreadyDeleted'
      }`) as string,
    );
    return;
  }

  eventBus.$once(eventNames.CONFIRMATION_DIALOG, () => {
    store.commit('setLoading', true);

    const promises = undeletedShipments.map(async (shipment) => {
      const res = await deleteShipmentHistory(
        shipment.trackingNumber,
      );

      const { carrierTrackingNumber } = shipment.packages[0];

      if (res.errorType === ActionsException.ShipmentIsInTransitException) {
        showWarningAlertDialog(
          i18n.t('history.alertDialog.cannotBeDeleted', {
            trackingNumber: carrierTrackingNumber,
          }) as string,
        );
      } else if ((res as HistoryTrackingResponse).successful) {
        showSuccessAlertDialog(
          i18n.t('history.alertDialog.shipmentSuccessfullyDeleted', {
            trackingNumber: carrierTrackingNumber,
          }) as string,
        );
        eventBus.$emit(eventNames.SHIPMENTS_DELETED);
      } else {
        showErrorAlertDialog(
          i18n.t('history.alertDialog.somethingWentWrongWith', { trackingNumber: carrierTrackingNumber }) as string,
        );
      }
    });

    Promise.all(promises).then(async () => {
      store.commit('setLoading', false);
    });
  });

  const content = i18n.t(`shipment.${shipments.length > 1 ? 'deleteShipmentsPrompt' : 'deleteShipmentPrompt'}`);

  store.commit('toggleConfirmDialog', {
    title: i18n.t('alertDialog.areYouSure'),
    content,
    btnText: i18n.t('buttons.delete'),
    show: true,
  });
};

export const duplicateShipmentAction = async (shipment: HistoryItemType): Promise<void> => {
  store.commit('setLoading', true);
  const res = await duplicateShipment(shipment.trackingNumber);

  if (!res.successful) {
    showErrorAlertDialog(res.errorMessage);
    store.commit('setLoading', false);
    return;
  }

  if (res?.isInProgressDetected) {
    showEditShipmentDialog(async () => {
      store.commit('setLoading', true);
      await duplicateShipment(shipment.trackingNumber, 1);
      await refetchInProgressDataAndSave();
      router.push({ name: 'shipment-create' });
      store.commit('setLoading', false);
    });
  } else {
    await refetchInProgressDataAndSave();
    router.push({ name: 'shipment-create' });
  }

  store.commit('setLoading', false);
};

export const reschedulePickupAction = async (
  shipment: HistoryItemType,
): Promise<boolean> => {
  if (shipment.deleted) {
    showWarningAlertDialog(i18n.t('history.alertDialog.cannotRescheduledDeletedShipment') as string);
    return false;
  }

  store.commit('setLoading', true);
  const res = await reschedulePickup(shipment.trackingNumber);
  store.commit('setLoading', false);

  if (res && res.successful && res.rescheduled) return true;

  showErrorAlertDialog(res?.errorMessage);
  return false;
};

export const getQueryStringForShipments = (
  initQueryString: string, queryParams: ShipmentsQueryParams,
): string => {
  let output = initQueryString;

  Object.entries(queryParams).forEach(([name, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((paramValue) => {
          output += `&${name}=${paramValue}`;
        });
      } else {
        output += `&${name}=${value}`;
      }
    }
  });

  return output;
};

export const getGoogleMapsUrlFromAddress = (
  address: {
    countryCode: string,
    postalCode?: string,
    city?: string,
    addr1: string,
    province?: string
},
): string => {
  const baseUrl = 'https://www.google.com/maps';
  const addressString = `${address.addr1}, ${`${address.city}, ` || ''}${`${address.province} ` || ''}${`${address.postalCode}, ` || ''}${address.countryCode}`;

  return `${baseUrl}?q=${addressString}&start=1&num=1`;
};

export const getShipmentMaps = async (
  fromAddress: ShipFrom,
  toAddress: ShipTo,
): Promise<{ fromMap: string, toMap: string }> => {
  let fromMap;
  let toMap;

  const fromCountryName = getCountryNameFromCode(fromAddress.countryCode);
  const toCountryName = getCountryNameFromCode(toAddress.countryCode);

  await Promise.all([
    (async function getFromMap() {
      fromMap = await getAddressMap({
        ...fromAddress,
        countryName: fromCountryName,
      });
    }()),
    (async function getToMap() {
      toMap = await getAddressMap({
        ...toAddress,
        countryName: toCountryName,
      });
    }()),
  ]);

  return { fromMap: fromMap.base64StringMap, toMap: toMap.base64StringMap };
};
